/**
 * Cross-brand checkout redirect (D8, D9, D24, D40).
 *
 * The server decides: `Car.checkoutBrandSite(pickupLocationId, returnLocationId)`
 * is null to stay on this brand, a domain to redirect there, or the
 * `AIRPORT_NOT_BOOKABLE` code when no brand taking part has every chosen
 * airport. The client only builds the URL and carries the source brand as
 * `referrer` (D11, D17).
 */

export const AIRPORT_NOT_BOOKABLE = 'AIRPORT_NOT_BOOKABLE';

function stripScheme(value: string): string {
  return value.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '').replace(/\/+$/, '');
}

/** Normalize a host or URL to the brand key: bare host, lowercase, `www.` stripped, port kept. */
export function normalizeReferralSiteMarker(value?: string | null): string | null {
  const trimmed = value?.trim().toLowerCase();
  if (!trimmed) return null;
  const withoutScheme = stripScheme(trimmed);
  const withoutWww = withoutScheme.replace(/^www\./, '');
  const host = withoutWww.split('/')[0].split('?')[0].split('#')[0].replace(/\/+$/, '');
  return host || null;
}

export function normalizeReferralSiteMarkerList(values?: string[] | null): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const value of values ?? []) {
    const marker = normalizeReferralSiteMarker(value);
    if (!marker || seen.has(marker)) continue;
    seen.add(marker);
    normalized.push(marker);
  }

  return normalized;
}

/** True when the host is a local dev origin that does not serve HTTPS. */
export function isLocalDevSiteHost(hostOrUrl: string): boolean {
  const host = stripScheme(hostOrUrl).split('/')[0].split(':')[0].toLowerCase();
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.endsWith('.localhost') ||
    /^192\.168\.\d{1,3}\.\d{1,3}$/.test(host) ||
    /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host)
  );
}

/** Pick http vs https for a brand host (bare host or full URL). */
export function resolveOwnerSiteScheme(siteUrl: string): 'http' | 'https' {
  const trimmed = siteUrl.trim();
  if (/^https:\/\//i.test(trimmed)) return 'https';
  if (/^http:\/\//i.test(trimmed)) return 'http';
  return isLocalDevSiteHost(trimmed) ? 'http' : 'https';
}

export function buildOwnerSiteBaseUrl(siteUrl: string): string {
  const trimmed = siteUrl.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed.replace(/\/+$/, '');
  }
  const host = stripScheme(trimmed);
  const scheme = resolveOwnerSiteScheme(host);
  return `${scheme}://${host}`;
}

/**
 * Parse a `brand=host` list, e.g. `lenvoy.co=localhost:3080,autopilotee.com=localhost:3000`.
 * Used for `REACT_APP_BRAND_DEV_HOSTS` (local dev) and `REACT_APP_BRAND_HOST_MAP` (staging).
 */
export function parseBrandDevHosts(raw?: string | null): Record<string, string> {
  const out: Record<string, string> = {};
  for (const pair of (raw ?? '').split(',')) {
    const [brand, host] = pair.split('=');
    const brandKey = normalizeReferralSiteMarker(brand);
    const hostKey = normalizeReferralSiteMarker(host);
    if (brandKey && hostKey) out[brandKey] = hostKey;
  }
  return out;
}

/**
 * `REACT_APP_BRAND_HOST_MAP`, e.g. `lenvoy.co=staging.lenvoy.co,autopilotee.com=staging.autopilotee.com`:
 * where each brand's production domain is served in a non-production
 * deployment such as staging. Ignored in a production build, so production
 * never redirects to a staging host even if the variable is set by mistake.
 */
export function readBrandHostMap(
  raw: string | null | undefined = process.env.REACT_APP_BRAND_HOST_MAP,
  apiEnv: string | null | undefined = process.env.REACT_APP_API_ENV,
): Record<string, string> {
  if ((apiEnv ?? '').trim().toLowerCase() === 'production') return {};
  return parseBrandDevHosts(raw);
}

/**
 * The host to send the browser to for a brand's production domain.
 * - On a local dev host, the brand's dev host from `REACT_APP_BRAND_DEV_HOSTS`
 *   (`lenvoy.co` becomes `localhost:3080`).
 * - Otherwise the brand's host from `REACT_APP_BRAND_HOST_MAP` when set
 *   (`lenvoy.co` becomes `staging.lenvoy.co` on staging).
 * - Otherwise the domain itself, as in production.
 */
export function resolveBrandRedirectHost(
  checkoutBrandSite: string,
  currentSiteHost: string,
  devHosts: Record<string, string> = parseBrandDevHosts(process.env.REACT_APP_BRAND_DEV_HOSTS),
  hostMap: Record<string, string> = readBrandHostMap(),
): string {
  const brand = normalizeReferralSiteMarker(checkoutBrandSite) ?? checkoutBrandSite;
  if (isLocalDevSiteHost(currentSiteHost) && devHosts[brand]) {
    return devHosts[brand];
  }
  if (hostMap[brand]) {
    return hostMap[brand];
  }
  return brand;
}

export type BrandRedirectDecision =
  | { kind: 'stay' }
  | { kind: 'airportNotBookable' }
  | { kind: 'redirect'; targetHost: string; targetBrandSite: string };

/**
 * Turn `Car.checkoutBrandSite` into a client decision (D40). The owner viewing
 * their own car never redirects; the server already skips them, but the car may
 * be cached from an anonymous session.
 */
export function decideBrandRedirect(options: {
  checkoutBrandSite?: string | null;
  currentSiteHost: string;
  carOwnerId?: string | null;
  currentUserId?: string | null;
  devHosts?: Record<string, string>;
  hostMap?: Record<string, string>;
}): BrandRedirectDecision {
  const value = options.checkoutBrandSite?.trim();
  if (!value) return { kind: 'stay' };
  if (value === AIRPORT_NOT_BOOKABLE) return { kind: 'airportNotBookable' };
  if (options.carOwnerId && options.currentUserId && options.carOwnerId === options.currentUserId) {
    return { kind: 'stay' };
  }
  const targetBrandSite = normalizeReferralSiteMarker(value) ?? value;
  const targetHost = resolveBrandRedirectHost(
    targetBrandSite,
    options.currentSiteHost,
    options.devHosts,
    options.hostMap,
  );
  const currentHost = normalizeReferralSiteMarker(options.currentSiteHost);
  // Already on the target: its production domain, or the host it is served
  // from here (dev host or staging host).
  if (
    currentHost &&
    (currentHost === targetBrandSite || currentHost === normalizeReferralSiteMarker(targetHost))
  ) {
    return { kind: 'stay' };
  }
  return { kind: 'redirect', targetBrandSite, targetHost };
}

export function buildOwnerCheckoutRedirectUrl(options: {
  ownerSiteUrl: string;
  carId: string;
  currentSearch?: string;
  referrerMarker: string;
  ssoCode?: string | null;
  includeHandoff?: boolean;
}): string {
  const base = buildOwnerSiteBaseUrl(options.ownerSiteUrl);
  const params = new URLSearchParams(options.currentSearch?.replace(/^\?/, '') ?? '');
  params.set('referrer', options.referrerMarker);
  if (options.includeHandoff !== false) {
    params.set('handoff', '1');
  }
  if (options.ssoCode) {
    params.set('sso_code', options.ssoCode);
  }
  const qs = params.toString();
  return `${base}/cars/guest/view/${options.carId}${qs ? `?${qs}` : ''}`;
}
